import {
  Box, Card, Stack, Typography, Chip, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// Hook that returns true on phone-sized viewports. Pages that need to
// flip layout based on width call this once and branch the JSX.
export function useIsMobile() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
}

const TNUM = { fontVariantNumeric: 'tabular-nums' };

// Compact card for one tabular row on phones. Renders as:
//   ┌──────────────────────────────────┐
//   │ TITLE                  [STATUS]  │   ← header row
//   │ subtitle (optional)              │
//   │ ──────────────────────────────── │
//   │ LABEL    value                   │   ← fields list
//   │ LABEL    value                   │
//   │ ──────────────────────────────── │
//   │ AMOUNT (optional, prominent)     │
//   │ ──────────────────────────────── │
//   │ [ actions row ]                  │
//   └──────────────────────────────────┘
export function MobileDataCard({
  title, subtitle, status, statusColor = 'default',
  fields = [], amount, amountColor = 'error.main', amountLabel,
  actions, accent, footer, onClick,
}) {
  return (
    <Card
      onClick={onClick}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: accent ? '4px solid' : '1px solid',
        borderLeftColor: accent || 'divider',
        boxShadow: 'none',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        } : undefined,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.4 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: subtitle ? 0.3 : 0.6 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontWeight: 800, fontSize: 13.5, lineHeight: 1.25,
              fontFamily: 'inherit',
              color: 'text.primary',
              wordBreak: 'break-word',
            }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{
                fontSize: 11.5, color: 'text.secondary',
                lineHeight: 1.3, mt: 0.2,
              }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {status && (
            <Chip size="small" color={statusColor}
              label={status}
              sx={{ height: 20, fontSize: 10, fontWeight: 800,
                '& .MuiChip-label': { px: 0.8 } }} />
          )}
        </Stack>

        {fields.length > 0 && (
          <>
            <Divider sx={{ my: 0.8 }} />
            <Box sx={{ display: 'grid', rowGap: 0.5 }}>
              {fields.map((f, i) => (
                <Box key={i} sx={{
                  display: 'grid',
                  gridTemplateColumns: '92px 1fr',
                  alignItems: 'baseline',
                  gap: 1,
                }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                    textTransform: 'uppercase', color: 'text.secondary',
                  }}>
                    {f.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: 12, fontWeight: f.bold ? 700 : 500,
                    color: f.color || 'text.primary',
                    wordBreak: 'break-word', ...TNUM,
                  }}>
                    {f.value ?? '—'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}

        {amount != null && (
          <>
            <Divider sx={{ my: 0.8 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography sx={{
                fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                textTransform: 'uppercase', color: 'text.secondary',
              }}>
                {amountLabel || 'Amount'}
              </Typography>
              <Typography sx={{
                fontSize: 16, fontWeight: 800, color: amountColor, ...TNUM,
              }}>
                {amount}
              </Typography>
            </Stack>
          </>
        )}

        {footer && (
          <>
            <Divider sx={{ my: 0.8 }} />
            <Box>{footer}</Box>
          </>
        )}

        {actions && (
          <>
            <Divider sx={{ my: 0.8 }} />
            <Stack direction="row" spacing={0.7} justifyContent="flex-end" flexWrap="wrap">
              {actions}
            </Stack>
          </>
        )}
      </Box>
    </Card>
  );
}

// Lightweight wrapper: renders desktop table on md+ and a stack of cards
// on phones. Pages keep their existing <Table> markup intact and supply a
// per-row `mobileCard` render that returns a <MobileDataCard /> (or any
// node). Pagination/empty states live outside this component — pass the
// already-paginated rows in.
export function ResponsiveTable({ rows = [], desktop, mobileCard, emptyMobile }) {
  const isMobile = useIsMobile();
  if (!isMobile) return desktop;
  if (rows.length === 0 && emptyMobile) return emptyMobile;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
      {rows.map((r, i) => mobileCard(r, i))}
    </Box>
  );
}

// Helper to color-code an amount cell consistently across pages.
export const moneyColor = (kind) =>
  kind === 'in'  ? 'success.main'
: kind === 'out' ? 'error.main'
:                  'text.primary';
